import React, { useState, useCallback, useMemo } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  IconButton,
  Tooltip,
  Avatar,
  Divider,
  Fade,
  Slide,
  Skeleton,
  useTheme,
  useMediaQuery,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Grid,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  Edit,
  Delete,
  Visibility,
  PlayArrow,
  Stop,
  Refresh,
  MoreVert,
  Star,
  StarBorder,
  ExpandMore,
  ExpandLess,
  Launch,
  ViewList as ListIcon,
  ViewModule as CardIcon,
  TableChart as TableIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Pause as PauseIcon,
  TrendingUp,
  TrendingDown,
  TrendingFlat,
} from '@mui/icons-material';
import { SAP_COLORS, SAP_SPACING, SAP_TYPOGRAPHY, SAP_SHADOWS } from '../../theme/sapDesignSystem';

// Unified interfaces supporting all component features
export interface DataField {
  key: string;
  label: string;
  render?: (value: any, row: any) => React.ReactNode;
  width?: string | number;
  align?: 'left' | 'center' | 'right';
  priority?: 'high' | 'medium' | 'low';
  sortable?: boolean;
  minWidth?: number;
  format?: (value: any) => React.ReactNode;
}

export interface DataColumn {
  id: string;
  label: string;
  minWidth?: number;
  align?: 'left' | 'center' | 'right';
  format?: (value: any) => React.ReactNode;
  sortable?: boolean;
}

export interface DataAction {
  id: string;
  label: string;
  icon: React.ReactNode;
  onClick: (row: any) => void;
  variant?: 'primary' | 'secondary' | 'success' | 'error';
  disabled?: (row: any) => boolean;
}

export type ViewVariant = 'table' | 'card' | 'list' | 'default' | 'compact' | 'detailed' | 'kanban' | 'metric';

interface EnhancedDataCardProps {
  // Data and display
  data: any[];
  fields?: DataField[];
  columns?: DataColumn[];
  variant?: ViewVariant;
  viewOptions?: ViewVariant[];
  onViewChange?: (variant: ViewVariant) => void;
  
  // Actions
  actions?: DataAction[] | ('view' | 'edit' | 'delete' | 'play' | 'stop' | 'refresh' | 'favorite')[];
  onAction?: (action: string, row: any) => void;
  
  // Basic card props (from DataCard)
  title?: string;
  subtitle?: string;
  icon?: React.ReactNode;
  children?: React.ReactNode;
  
  // Dashboard card props
  value?: string | number;
  trend?: string;
  trendDirection?: 'up' | 'down' | 'neutral';
  status?: 'healthy' | 'warning' | 'error' | 'info' | 'success';
  showTrend?: boolean;
  showStatus?: boolean;
  metric?: {
    unit?: string;
    precision?: number;
    format?: 'number' | 'currency' | 'percentage' | 'duration';
  };
  
  // Enhanced features
  loading?: boolean;
  error?: string | null;
  emptyMessage?: string;
  onRowClick?: (row: any) => void;
  showViewToggle?: boolean;
  showSearch?: boolean;
  showFilters?: boolean;
  showSorting?: boolean;
  onSort?: (field: string, direction: 'asc' | 'desc') => void;
  sortField?: string;
  sortDirection?: 'asc' | 'desc';
  maxHeight?: number | string;
  selectable?: boolean;
  onSelectionChange?: (selectedRows: any[]) => void;
  selectedRows?: any[];
  groupBy?: string;
  showGroupHeaders?: boolean;
  cardWidth?: number | string;
  responsive?: boolean;
  size?: 'small' | 'medium' | 'large';
  onClick?: () => void;
}

const getActionIcon = (action: string) => {
  switch (action) {
    case 'view':
      return <Visibility />;
    case 'edit':
      return <Edit />;
    case 'delete':
      return <Delete />;
    case 'play':
      return <PlayArrow />;
    case 'stop':
      return <Stop />;
    case 'refresh':
      return <Refresh />;
    case 'favorite':
      return <StarBorder />;
    default:
      return <MoreVert />;
  }
};

const getActionColor = (action: string, theme: any) => {
  switch (action) {
    case 'view':
      return theme.palette.info.main;
    case 'edit':
      return theme.palette.primary.main;
    case 'delete':
      return theme.palette.error.main;
    case 'play':
      return theme.palette.success.main;
    case 'stop':
      return theme.palette.warning.main;
    case 'refresh':
      return theme.palette.info.main;
    case 'favorite':
      return theme.palette.warning.main;
    default:
      return theme.palette.primary.main;
  }
};

const renderFieldValue = (field: DataField, value: any, row: any, theme: any) => {
  if (field.render) {
    return field.render(value, row);
  }

  // Default rendering based on field type
  if (field.key === 'status') {
    const statusColors = {
      active: theme.palette.success.main,
      inactive: theme.palette.error.main,
      warning: theme.palette.warning.main,
      pending: theme.palette.info.main,
      completed: theme.palette.success.main,
      failed: theme.palette.error.main,
    };

    return (
      <Chip
        label={value}
        size="small"
        sx={{
          backgroundColor: statusColors[value as keyof typeof statusColors] || theme.palette.info.main,
          color: theme.palette.common.white,
          fontWeight: 600,
          fontSize: '11px',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          
          '&:hover': {
            transform: 'scale(1.05)',
            boxShadow: theme.shadows[4],
          },
        }}
      />
    );
  }

  if (field.key === 'avatar' || field.key === 'image') {
    return (
      <Avatar
        src={value}
        sx={{
          width: 32,
          height: 32,
          border: `2px solid ${theme.palette.divider}`,
          transition: 'all 0.3s ease',
          
          '&:hover': {
            transform: 'scale(1.1)',
            borderColor: theme.palette.primary.main,
          },
        }}
      >
        {value ? undefined : value?.charAt(0)?.toUpperCase()}
      </Avatar>
    );
  }

  if (field.key === 'date' || field.key === 'timestamp') {
    return (
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
        {new Date(value).toLocaleDateString()}
      </Typography>
    );
  }

  if (field.key === 'number' || field.key === 'amount') {
    return (
      <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
        {typeof value === 'number' ? value.toLocaleString() : value}
      </Typography>
    );
  }

  return (
    <Typography variant="body2" sx={{ color: theme.palette.text.primary }}>
      {value}
    </Typography>
  );
};

export default function EnhancedDataCard({
  data = [],
  fields,
  columns,
  variant = 'default',
  viewOptions = ['table', 'card', 'list'],
  onViewChange,
  actions = [],
  onAction,
  title,
  subtitle,
  icon,
  children,
  value,
  trend,
  trendDirection = 'neutral',
  status = 'info',
  showTrend = true,
  showStatus = true,
  metric,
  loading = false,
  error = null,
  emptyMessage = 'No data available',
  onRowClick,
  showViewToggle = true,
  showSearch = false,
  showFilters = false,
  showSorting = false,
  onSort,
  sortField,
  sortDirection = 'asc',
  maxHeight,
  selectable = false,
  onSelectionChange,
  selectedRows = [],
  groupBy,
  showGroupHeaders = true,
  cardWidth,
  responsive = true,
  size = 'medium',
  onClick,
}: EnhancedDataCardProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const [favorites, setFavorites] = useState<Set<number>>(new Set());
  const [currentView, setCurrentView] = useState<ViewVariant>(variant);

  // Determine component mode based on props
  const isDashboardCard = !data.length && (value !== undefined || title);
  const isBasicCard = !data.length && !value && children;
  const isDataCard = data.length > 0 || fields || columns;

  // Convert columns to fields if needed
  const displayFields = useMemo(() => {
    if (fields) return fields;
    if (columns) {
      return columns.map(col => ({
        key: col.id,
        label: col.label,
        align: col.align,
        minWidth: col.minWidth,
        format: col.format,
        sortable: col.sortable,
        priority: 'medium' as const,
      }));
    }
    return [];
  }, [fields, columns]);

  // Handle view change
  const handleViewChange = useCallback((event: React.MouseEvent<HTMLElement>, newView: ViewVariant | null) => {
    if (newView) {
      setCurrentView(newView);
      onViewChange?.(newView);
    }
  }, [onViewChange]);

  const handleRowToggle = (index: number) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedRows(newExpanded);
  };

  const handleFavoriteToggle = (index: number) => {
    const newFavorites = new Set(favorites);
    if (newFavorites.has(index)) {
      newFavorites.delete(index);
    } else {
      newFavorites.add(index);
    }
    setFavorites(newFavorites);
  };

  const handleSelectionToggle = (row: any, index: number) => {
    if (!onSelectionChange) return;
    
    const newSelected = [...selectedRows];
    const existingIndex = newSelected.findIndex(item => item.id === row.id);
    
    if (existingIndex >= 0) {
      newSelected.splice(existingIndex, 1);
    } else {
      newSelected.push(row);
    }
    
    onSelectionChange(newSelected);
  };

  const isSelected = (row: any) => {
    return selectedRows.some(selected => selected.id === row.id);
  };

  // Handle row click
  const handleRowClick = useCallback((row: any) => {
    onRowClick?.(row);
  }, [onRowClick]);

  const groupedData = groupBy ? data.reduce((acc, row) => {
    const group = row[groupBy] || 'Other';
    if (!acc[group]) acc[group] = [];
    acc[group].push(row);
    return acc;
  }, {} as Record<string, any[]>) : { 'All': data };

  // Render basic card (from DataCard)
  const renderBasicCard = () => (
    <Card
      sx={{
        borderRadius: 2,
        border: 1,
        borderColor: 'divider',
        boxShadow: 2,
        transition: 'all 0.2s ease',
        '&:hover': {
          boxShadow: 4,
          transform: 'translateY(-2px)',
        },
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Typography
          variant="h6"
          sx={{
            color: 'text.primary',
            fontWeight: 'semibold',
            mb: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Box sx={{ color: 'primary.main' }}>{icon}</Box>
          {title}
        </Typography>
        {children}
      </CardContent>
    </Card>
  );

  // Render dashboard card (from DashboardCard)
  const renderDashboardCard = () => {
    const sizeConfig = {
      small: { padding: 2, titleSize: 'body2', valueSize: 'h6', subtitleSize: 'caption', iconSize: 24, chipHeight: 20 },
      medium: { padding: 3, titleSize: 'body1', valueSize: 'h4', subtitleSize: 'body2', iconSize: 32, chipHeight: 24 },
      large: { padding: 4, titleSize: 'h6', valueSize: 'h3', subtitleSize: 'body1', iconSize: 40, chipHeight: 28 },
    };
    const currentSize = sizeConfig[size];

    const formatValue = (val: string | number, metric?: any) => {
      if (!metric) return val;
      const numValue = typeof val === 'string' ? parseFloat(val) : val;
      switch (metric.format) {
        case 'currency': return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: metric.precision || 2 }).format(numValue);
        case 'percentage': return `${numValue.toFixed(metric.precision || 1)}%`;
        case 'duration': return `${Math.floor(numValue / 60)}:${(numValue % 60).toString().padStart(2, '0')}`;
        default: return metric.unit ? `${numValue.toFixed(metric.precision || 0)}${metric.unit}` : numValue;
      }
    };

    const getStatusColor = (status: string) => {
      switch (status) {
        case 'healthy': case 'success': return theme.palette.success.main;
        case 'warning': return theme.palette.warning.main;
        case 'error': return theme.palette.error.main;
        case 'info': return theme.palette.info.main;
        default: return theme.palette.primary.main;
      }
    };

    const getTrendColor = (direction: string) => {
      switch (direction) {
        case 'up': return theme.palette.success.main;
        case 'down': return theme.palette.error.main;
        default: return theme.palette.text.secondary;
      }
    };

    const getTrendIcon = (direction: string) => {
      switch (direction) {
        case 'up': return <TrendingUp />;
        case 'down': return <TrendingDown />;
        default: return <TrendingFlat />;
      }
    };

    return (
      <Fade in={true} timeout={600}>
        <Card
          sx={{
            background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.background.default} 100%)`,
            borderRadius: theme.spacing(2.5),
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: theme.shadows[4],
            cursor: onClick ? 'pointer' : 'default',
            transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              background: `linear-gradient(90deg, ${getStatusColor(status)} 0%, ${theme.palette.primary.main} 100%)`,
              opacity: 0.8,
            },
            '&:hover': onClick ? {
              transform: 'translateY(-4px)',
              boxShadow: theme.shadows[12],
              borderColor: getStatusColor(status),
            } : {},
          }}
          onClick={onClick}
        >
          <CardContent sx={{ p: currentSize.padding }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant={currentSize.titleSize as any} color="text.secondary" sx={{ fontWeight: 600, mb: 1, textTransform: 'uppercase', letterSpacing: '0.5px', opacity: 0.8 }}>
                  {title}
                </Typography>
                <Typography variant={currentSize.valueSize as any} sx={{ fontWeight: 800, color: theme.palette.text.primary, mb: 1, lineHeight: 1.2 }}>
                  {formatValue(value!, metric)}
                </Typography>
                {subtitle && (
                  <Typography variant={currentSize.subtitleSize as any} color="text.secondary" sx={{ fontSize: currentSize.subtitleSize === 'body2' ? '14px' : '12px', fontWeight: 500, opacity: 0.7 }}>
                    {subtitle}
                  </Typography>
                )}
              </Box>
              {icon && (
                <Box sx={{ ml: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', width: currentSize.iconSize, height: currentSize.iconSize, borderRadius: '50%', background: `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`, color: theme.palette.primary.contrastText, boxShadow: theme.shadows[4] }}>
                  {icon}
                </Box>
              )}
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
              {showTrend && trend && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, padding: '4px 8px', borderRadius: 1, background: `linear-gradient(135deg, ${getTrendColor(trendDirection)}10 0%, ${getTrendColor(trendDirection)}05 100%)`, border: `1px solid ${getTrendColor(trendDirection)}30` }}>
                  <Box sx={{ color: getTrendColor(trendDirection) }}>{getTrendIcon(trendDirection)}</Box>
                  <Typography variant="body2" sx={{ color: getTrendColor(trendDirection), fontWeight: 700, fontSize: currentSize.subtitleSize === 'body2' ? '14px' : '12px' }}>
                    {trend}
                  </Typography>
                </Box>
              )}
              {showStatus && (
                <Chip label={status} size="small" sx={{ backgroundColor: getStatusColor(status), color: theme.palette.common.white, fontWeight: 700, fontSize: currentSize.subtitleSize === 'body2' ? '12px' : '11px', height: currentSize.chipHeight }} />
              )}
            </Box>
          </CardContent>
        </Card>
      </Fade>
    );
  };

  if (loading) {
    return (
      <Box sx={{ width: '100%' }}>
        {Array.from({ length: 3 }).map((_, index) => (
          <Card key={index} sx={{ mb: 2, p: 2 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Skeleton variant="circular" width={40} height={40} />
              <Box sx={{ flex: 1 }}>
                <Skeleton variant="text" width="60%" height={20} />
                <Skeleton variant="text" width="40%" height={16} />
              </Box>
              <Skeleton variant="rectangular" width={80} height={24} />
            </Box>
          </Card>
        ))}
      </Box>
    );
  }

  // Determine which render mode to use
  if (isBasicCard) {
    return renderBasicCard();
  }

  if (isDashboardCard) {
    return renderDashboardCard();
  }

  // For data cards, use the existing complex rendering logic
  return (
    <Box sx={{ width: '100%' }}>
      {/* Header */}
      {(title || subtitle) && (
        <Slide direction="down" in={true} timeout={500}>
          <Box sx={{ 
            mb: 3, 
            p: 3, 
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            borderRadius: theme.spacing(2),
            color: theme.palette.primary.contrastText,
            position: 'relative',
            overflow: 'hidden',
            
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              right: 0,
              width: '80px',
              height: '80px',
              background: 'rgba(255, 255, 255, 0.1)',
              borderRadius: '50%',
              transform: 'translate(20px, -20px)',
            },
          }}>
            {title && (
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                {title}
              </Typography>
            )}
            {subtitle && (
              <Typography variant="body1" sx={{ opacity: 0.9 }}>
                {subtitle}
              </Typography>
            )}
          </Box>
        </Slide>
      )}

      {/* Data Cards */}
      <Box sx={{ 
        display: 'grid',
        gridTemplateColumns: responsive ? {
          xs: '1fr',
          sm: 'repeat(auto-fill, minmax(300px, 1fr))',
          md: 'repeat(auto-fill, minmax(350px, 1fr))',
          lg: 'repeat(auto-fill, minmax(400px, 1fr))',
        } : `repeat(auto-fill, minmax(${cardWidth || 350}px, 1fr))`,
        gap: 2,
        maxHeight,
        overflow: 'auto',
      }}>
        {Object.entries(groupedData).map(([groupName, groupData]) => (
          <React.Fragment key={groupName}>
            {/* Group Header */}
            {showGroupHeaders && groupBy && (
              <Box sx={{ 
                gridColumn: '1 / -1',
                p: 2,
                background: `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${theme.palette.background.paper} 100%)`,
                borderRadius: theme.spacing(1.5),
                border: `1px solid ${theme.palette.divider}`,
                mb: 1,
              }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                  {groupName} ({(groupData as any[]).length})
                </Typography>
              </Box>
            )}

            {/* Data Cards */}
            {(groupData as any[]).map((row: any, index: number) => (
              <Fade key={row.id || index} in={true} timeout={600 + index * 100}>
                <Card
                  sx={{
                    background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.background.default} 100%)`,
                    borderRadius: theme.spacing(2.5),
                    border: `2px solid ${isSelected(row) ? theme.palette.primary.main : theme.palette.divider}`,
                    boxShadow: theme.shadows[4],
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    position: 'relative',
                    overflow: 'hidden',
                    cursor: selectable ? 'pointer' : 'default',
                    
                    '&:hover': {
                      boxShadow: theme.shadows[8],
                      transform: 'translateY(-4px)',
                      borderColor: theme.palette.primary.main,
                    },
                    
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: '3px',
                      background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                      opacity: 0,
                      transition: 'opacity 0.3s ease',
                    },
                    
                    '&:hover::before': {
                      opacity: 1,
                    },
                  }}
                  onClick={() => selectable && handleSelectionToggle(row, index)}
                >
                  <CardContent sx={{ p: variant === 'compact' ? 2 : 3 }}>
                    {/* Main Content */}
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'flex-start',
                      mb: variant === 'compact' ? 1 : 2,
                    }}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        {/* Primary Fields */}
                        {displayFields
                          .filter(field => field.priority === 'high')
                          .map(field => (
                            <Box key={field.key} sx={{ mb: 1 }}>
                              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>
                                {field.label}
                              </Typography>
                              <Box sx={{ mt: 0.5 }}>
                                {renderFieldValue(field, row[field.key], row, theme)}
                              </Box>
                            </Box>
                          ))}
                      </Box>

                      {/* Action Buttons */}
                      <Box sx={{ display: 'flex', gap: 0.5, ml: 2 }}>
                        {Array.isArray(actions) && (actions as any[]).includes('favorite') && (
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleFavoriteToggle(index);
                            }}
                            sx={{
                              color: favorites.has(index) ? theme.palette.warning.main : theme.palette.text.secondary,
                              transition: 'all 0.3s ease',
                              
                              '&:hover': {
                                transform: 'scale(1.1)',
                                color: theme.palette.warning.main,
                              },
                            }}
                          >
                            {favorites.has(index) ? <Star /> : <StarBorder />}
                          </IconButton>
                        )}

                        {Array.isArray(actions) && (actions as any[]).includes('view') && (
                          <Tooltip title="View Details">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                onAction?.('view', row);
                              }}
                              sx={{
                                color: getActionColor('view', theme),
                                transition: 'all 0.3s ease',
                                
                                '&:hover': {
                                  transform: 'scale(1.1)',
                                  boxShadow: theme.shadows[4],
                                },
                              }}
                            >
                              <Visibility />
                            </IconButton>
                          </Tooltip>
                        )}

                        {variant === 'detailed' && (
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRowToggle(index);
                            }}
                            sx={{
                              color: theme.palette.text.secondary,
                              transition: 'all 0.3s ease',
                              
                              '&:hover': {
                                transform: 'scale(1.1)',
                                color: theme.palette.primary.main,
                              },
                            }}
                          >
                            {expandedRows.has(index) ? <ExpandLess /> : <ExpandMore />}
                          </IconButton>
                        )}
                      </Box>
                    </Box>

                    {/* Secondary Fields */}
                    {variant !== 'compact' && (
                      <Box sx={{ 
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
                        gap: 2,
                        mb: variant === 'detailed' ? 2 : 0,
                      }}>
                        {displayFields
                          .filter(field => field.priority === 'medium')
                          .map(field => (
                            <Box key={field.key}>
                              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                                {field.label}
                              </Typography>
                              <Box sx={{ mt: 0.5 }}>
                                {renderFieldValue(field, row[field.key], row, theme)}
                              </Box>
                            </Box>
                          ))}
                      </Box>
                    )}

                    {/* Expanded Details */}
                    {variant === 'detailed' && expandedRows.has(index) && (
                      <Slide direction="up" in={true} timeout={400}>
                        <Box>
                          <Divider sx={{ my: 2 }} />
                          <Box sx={{ 
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                            gap: 2,
                          }}>
                            {displayFields
                              .filter(field => field.priority === 'low')
                              .map(field => (
                                <Box key={field.key}>
                                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                                    {field.label}
                                  </Typography>
                                  <Box sx={{ mt: 0.5 }}>
                                    {renderFieldValue(field, row[field.key], row, theme)}
                                  </Box>
                                </Box>
                              ))}
                          </Box>
                        </Box>
                      </Slide>
                    )}

                    {/* Action Bar */}
                    {actions.length > 0 && variant !== 'compact' && (
                      <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'flex-end', 
                        gap: 1, 
                        mt: 2,
                        pt: 2,
                        borderTop: `1px solid ${theme.palette.divider}`,
                      }}>
                        {Array.isArray(actions) && actions
                          .filter(action => typeof action === 'string' && action !== 'favorite' && action !== 'view')
                          .map(action => (
                            <Tooltip key={action} title={action.charAt(0).toUpperCase() + action.slice(1)}>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onAction?.(action, row);
                                }}
                                sx={{
                                  color: getActionColor(action, theme),
                                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                  
                                  '&:hover': {
                                    backgroundColor: `${getActionColor(action, theme)}20`,
                                    transform: 'scale(1.1)',
                                    boxShadow: theme.shadows[4],
                                  },
                                }}
                              >
                                {getActionIcon(action)}
                              </IconButton>
                            </Tooltip>
                          ))}
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Fade>
            ))}
          </React.Fragment>
        ))}
      </Box>

      {/* Empty State */}
      {data.length === 0 && !loading && (
        <Fade in={true} timeout={800}>
          <Box sx={{ 
            textAlign: 'center', 
            py: 8,
            background: `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${theme.palette.background.paper} 100%)`,
            borderRadius: theme.spacing(2),
            border: `2px dashed ${theme.palette.divider}`,
          }}>
            <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 600, mb: 2 }}>
              {emptyMessage}
            </Typography>
            <Typography variant="body2" color="text.disabled">
              No data found matching your criteria
            </Typography>
          </Box>
        </Fade>
      )}
    </Box>
  );
}
